import { Component } from '@theme/component';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/** One scroll position drives the editorial story, photograph, navigation and ring.
 * @extends {Component<{scene: HTMLElement, left: HTMLElement, right: HTMLElement, status: HTMLElement}>}
 */
class OzmoUniverses extends Component {
  requiredRefs = ['scene', 'left', 'right', 'status'];
  #controller;
  #frame = 0;
  #active = -1;
  #initialized = false;
  #lastSavedChapter = -1;
  #restoreProgress = null;
  #restoreFrame = 0;
  #navigationPending = false;
  #interacted = false;
  #reduce = matchMedia('(prefers-reduced-motion: reduce)');
  #items = [];
  #buttons = [];
  #photos = [];
  #panels = [];
  #actions = [];

  connectedCallback() { super.connectedCallback(); this.#start(); }
  updatedCallback() { super.updatedCallback(); this.#start(); }
  #start() {
    this.#controller?.abort();
    cancelAnimationFrame(this.#restoreFrame);
    this.#restoreFrame = 0;
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const backNavigation = performance.getEntriesByType('navigation')[0]?.type === 'back_forward';
    this.#navigationPending = !this.#initialized && backNavigation;
    this.#restoreProgress = this.#navigationPending ? this.#readChapter() : null;
    this.#initialized = true;
    this.#interacted = false;
    this.#lastSavedChapter = -1;
    this.#items = [...this.querySelectorAll('[data-universe-item]')];
    this.#buttons = [...this.querySelectorAll('[data-universe-index]')];
    this.#photos = [...this.querySelectorAll('[data-universe-photo]')];
    this.#panels = [...this.querySelectorAll('[data-universe-panel]')];
    this.#actions = [...this.querySelectorAll('[data-universe-action]')];
    if (!this.#items.length) return;
    this.setAttribute('data-universes-enhanced', '');
    for (const button of this.#buttons) button.removeAttribute('disabled');
    window.addEventListener('scroll', this.#schedule, { passive: true, capture: true, signal });
    window.addEventListener('resize', this.#measure, { passive: true, signal });
    window.addEventListener('pagehide', this.#prepareBack, { signal });
    window.addEventListener('pageshow', this.#restoreBack, { signal });
    window.addEventListener('wheel', this.#cancelRestoration, { passive: true, signal });
    window.addEventListener('touchstart', this.#cancelRestoration, { passive: true, signal });
    this.#reduce.addEventListener('change', this.#measure, { signal });
    this.addEventListener('keydown', this.#keyboard, { signal });
    document.addEventListener('shopify:block:select', this.#editorSelect, { signal });
    this.#active = -1;
    this.#activate(0);
    this.#measure();
    document.fonts?.ready.then(() => {
      if (!this.isConnected || signal.aborted) return;
      this.#measure();
      if (backNavigation && this.#navigationPending) this.#restoreBack();
    });
  }
  #measure = () => {
    this.removeAttribute('data-flow');
    // Short viewports and reduced motion keep the full content in normal flow.
    if (this.#reduce.matches || this.refs.scene.scrollHeight > this.refs.scene.clientHeight + 4) {
      this.setAttribute('data-flow', '');
    }
    this.#schedule();
  };
  #schedule = () => {
    if (!this.#frame) this.#frame = requestAnimationFrame(this.#update);
  };
  #geometry() {
    const rect = this.getBoundingClientRect();
    const offset = parseFloat(getComputedStyle(this.refs.scene).top) || 0;
    const range = Math.max(1, rect.height - this.refs.scene.getBoundingClientRect().height);
    return { rect, offset, range, start: getScrollTop() + rect.top - offset };
  }
  #update = () => {
    this.#frame = 0;
    if (!this.#items.length || this.hasAttribute('data-flow')) return;
    const { rect, offset, range } = this.#geometry();
    const rawProgress = (offset - rect.top) / range;
    if (rawProgress < 0 || rawProgress > 1) this.#clearChapterSnapshot();
    if (rect.top > innerHeight || rect.bottom < 0) return;
    const progress = Math.max(0, Math.min(1, rawProgress));
    this.refs.scene.style.setProperty('--u-progress', String(progress));
    const index = Math.min(this.#items.length - 1, Math.floor(progress * this.#items.length));
    this.#activate(index);
    if (rawProgress >= 0 && rawProgress <= 1) this.#saveChapter(progress, index);
  };
  // Store a navigation snapshot only at chapter boundaries while the document is
  // active. WebKit may reject history writes in pagehide. Scroll remains the live
  // source of truth; this snapshot is consumed only on browser back/forward.
  #snapshotKey() { return `ozmo-universe:${location.pathname}:${this.id}`; }
  #readChapter() {
    const progress = history.state?.ozmoUniverseProgress?.[this.id];
    if (Number.isFinite(progress)) return progress;
    try {
      const saved = JSON.parse(sessionStorage.getItem(this.#snapshotKey()) || 'null');
      return saved && Date.now() - saved.time < 1800000 ? saved.progress : null;
    } catch { return null; }
  }
  #saveChapter(progress, index) {
    if (this.#navigationPending || this.#lastSavedChapter === index) return;
    this.#lastSavedChapter = index;
    try { sessionStorage.setItem(this.#snapshotKey(), JSON.stringify({ progress, time: Date.now() })); } catch { /* Optional tab-local back navigation fallback. */ }
    try {
      const state = typeof history.state === 'object' && history.state !== null ? history.state : {};
      history.replaceState({ ...state, ozmoUniverseProgress: { ...state.ozmoUniverseProgress, [this.id]: progress } }, '');
    } catch { /* Restricted history must never disable the switcher. */ }
  }
  #clearChapterSnapshot() {
    if (this.#navigationPending) return;
    this.#lastSavedChapter = -1;
    try { sessionStorage.removeItem(this.#snapshotKey()); } catch { /* Storage may be restricted. */ }
    try {
      const state = history.state;
      if (state?.ozmoUniverseProgress?.[this.id] === undefined) return;
      const chapters = { ...state.ozmoUniverseProgress };
      delete chapters[this.id];
      history.replaceState({ ...state, ozmoUniverseProgress: chapters }, '');
    } catch { /* Keep ordinary page navigation available in restricted contexts. */ }
  }
  #prepareBack = () => {
    cancelAnimationFrame(this.#restoreFrame);
    this.#restoreFrame = 0;
    this.#restoreProgress = this.#readChapter();
    this.#navigationPending = true;
    this.#interacted = false;
  };
  #cancelRestoration = () => {
    this.#interacted = true;
    this.#navigationPending = false;
    cancelAnimationFrame(this.#restoreFrame);
    this.#restoreFrame = 0;
  };
  /** @param {PageTransitionEvent} [event] */
  #restoreBack = (event) => {
    const back = event?.persisted || performance.getEntriesByType('navigation')[0]?.type === 'back_forward';
    if (!back || this.#interacted || this.#restoreFrame) return;
    const progress = this.#restoreProgress ?? this.#readChapter();
    if (!Number.isFinite(progress) || progress < 0 || progress > 1) {
      this.#navigationPending = false;
      return;
    }
    this.#navigationPending = true;
    this.#restoreFrame = requestAnimationFrame(() => {
      this.#restoreFrame = requestAnimationFrame(() => {
        this.#restoreFrame = 0;
        if (!this.isConnected || this.#interacted) return;
        if (this.hasAttribute('data-flow')) {
          this.#activate(Math.min(this.#items.length - 1, Math.floor(progress * this.#items.length)));
          this.refs.scene.style.setProperty('--u-progress', String(progress));
        } else {
          const { start, range } = this.#geometry();
          scrollTo({ top: start + range * progress, behavior: 'instant' });
        }
        this.#navigationPending = false;
        this.#lastSavedChapter = -1;
        this.#schedule();
      });
    });
  };
  #activate(index, announce = false) {
    if (index === this.#active) return;
    this.#active = index;
    this.dataset.activeIndex = String(index);
    const active = this.#items[index];
    this.refs.scene.style.setProperty('--u-ring', active?.dataset.ring || 'var(--ozmo-moka)');
    this.refs.scene.style.setProperty('--u-band', active?.dataset.band || 'var(--ozmo-nude)');
    this.#items.forEach((item, i) => item.classList.toggle('is-active', i === index));
    this.#buttons.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
    this.#photos.forEach((photo, i) => {
      photo.classList.toggle('is-active', i === index);
      photo.setAttribute('aria-hidden', String(i !== index));
    });
    for (const group of [this.#panels, this.#actions]) {
      group.forEach((node, i) => {
        node.classList.toggle('is-active', i === index);
        node.toggleAttribute('inert', i !== index);
        node.setAttribute('aria-hidden', String(i !== index));
      });
    }
    const list = active?.parentElement;
    if (active instanceof HTMLElement && list && list.scrollWidth > list.clientWidth + 1) {
      const itemRect = active.getBoundingClientRect();
      const listRect = list.getBoundingClientRect();
      list.scrollBy({ left: itemRect.left - listRect.left - (listRect.width - itemRect.width) / 2, behavior: this.#reduce.matches ? 'instant' : 'smooth' });
    }
    if (announce) this.refs.status.textContent = this.#buttons[index]?.textContent.trim().replace(/\s+/g, ' ') || '';
  }
  #select(index) {
    if (!Number.isInteger(index) || index < 0 || index >= this.#items.length) return;
    this.#cancelRestoration();
    this.#activate(index, true);
    if (this.hasAttribute('data-flow')) {
      this.refs.scene.style.setProperty('--u-progress', String(index / Math.max(1, this.#items.length - 1)));
      return;
    }
    const { start, range } = this.#geometry();
    // Land in the middle of the corresponding scroll segment. No separate timeline
    // or locked animation can drift away from the next native scroll event.
    scrollTo({ top: start + range * (index + .5) / this.#items.length, behavior: 'instant' });
    this.#schedule();
  }
  /** Select without following the separate commercial destination. @param {MouseEvent} event */
  selectUniverse(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-universe-index]') : null;
    if (!(button instanceof HTMLButtonElement)) return;
    button.focus({ preventScroll: true });
    this.#select(Number(button.dataset.universeIndex));
  }
  /** @param {KeyboardEvent} event */
  #keyboard = (event) => {
    if (!(event.target instanceof HTMLButtonElement) || !event.target.hasAttribute('data-universe-index')) return;
    const rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    let next = Number(event.target.dataset.universeIndex);
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.#items.length - 1;
    else if (event.key === 'ArrowDown') next++;
    else if (event.key === 'ArrowUp') next--;
    else if (event.key === 'ArrowRight') next += rtl;
    else if (event.key === 'ArrowLeft') next -= rtl;
    else return;
    event.preventDefault();
    next = (next + this.#items.length) % this.#items.length;
    this.#buttons[next]?.focus({ preventScroll: true });
    this.#select(next);
  };
  /** @param {CustomEvent<{blockId: string}>} event */
  #editorSelect = (event) => {
    const index = this.#items.findIndex(item => item.dataset.blockId === event.detail?.blockId);
    if (index >= 0) this.#select(index);
  };
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    cancelAnimationFrame(this.#restoreFrame);
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#restoreFrame = 0;
  }
}
if (!customElements.get('ozmo-universes')) customElements.define('ozmo-universes', OzmoUniverses);
